import React, { Dispatch, useMemo } from "react";
import { useImmer } from "use-immer";

import { IgButton, IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { hashGroupByKey } from "@intelligems/ig-utils";
import { Grid } from "@mui/material";
import { ChooseCouponField } from "@src/features/admin/components/Fields/ChooseCouponField";
import {
  CurrencyFormat,
  DecimalFormat,
} from "@src/features/admin/components/utils";
import { CreateCoupon } from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/PricesTab/CreateCoupon";
import { PriceAccordion } from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/PricesTab/PriceAccordion/PriceAccordion";
import {
  CreateOrModifySubscriptionReducerType,
  CreateSubscriptionReducerAction,
} from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/subscriptionReducer";

interface ChoosePricesProps {
  index: number;
  payload: CreateOrModifySubscriptionReducerType;
  dispatchPayload: Dispatch<CreateSubscriptionReducerAction>;
}

const CouponSummary = ({
  payload,
}: Omit<ChoosePricesProps, "dispatchPayload">) => {
  let discount: string;
  let duration: string;

  const coupon = payload.phases[payload.phaseIndex].coupon;
  if (!coupon) {
    return null;
  }

  if (coupon.amountOff) {
    discount = `${CurrencyFormat.format(coupon.amountOff / 100)} off`;
  } else {
    discount = `${coupon.percentOff}% off`;
  }

  if (coupon.durationInMonths) {
    duration = ` for ${coupon.durationInMonths} months.`;
  } else if (coupon.duration === "forever") {
    duration = " forever.";
  } else if (coupon.duration === "once") {
    duration = " one time.";
  } else {
    duration = ".";
  }

  return (
    <IgTypography variant={"body2"}>
      <IgTypography variant={"body2"} sx={{ fontWeight: 600 }}>
        {coupon.name}
      </IgTypography>
      {" Coupon "}- {discount}
      {duration}
    </IgTypography>
  );
};

export const ChoosePrices = ({
  index,
  payload,
  dispatchPayload,
}: ChoosePricesProps) => {
  const phase = payload.phases[payload.phaseIndex];
  const [showCreateCoupon, setShowCreateCoupon] = useImmer<boolean>(false);

  const onCouponSuccess = () => {
    setShowCreateCoupon(false);
  };

  const { data, allData } = useMemo(() => {
    if (!phase.items.length) {
      return { data: {}, allData: [] };
    }

    const d = phase.items
      .filter((i) => !i.deleted)
      .map((i, idx) => ({ ...i, index: idx }));
    return {
      data: hashGroupByKey(d, (_) => _.product.id),
      allData: d,
    };
  }, [phase.items]);

  return (
    <Grid container item direction={"column"} rowSpacing={2}>
      <Grid item>
        <IgTypography>
          {Object.keys(data).length > 1
            ? "Choose Prices for Products"
            : "Choose Price for Product"}
        </IgTypography>
      </Grid>
      {payload.usages ? (
        <Grid item>
          <IgTypography variant={"body2"}>
            This organization has{" "}
            {DecimalFormat.format(payload.usages.ordersPerMonth)} orders / month
          </IgTypography>
        </Grid>
      ) : null}
      <Grid container item alignItems={"center"}>
        <Grid item flex={1}>
          <ChooseCouponField
            payload={payload}
            dispatchPayload={dispatchPayload}
          />
        </Grid>
        <Grid item>
          <IgTooltip title={"Create Coupon"}>
            <span>
              <IgButton
                variant={"text"}
                onClick={() => {
                  dispatchPayload({ type: "setCoupon", value: null });
                  setShowCreateCoupon((draft) => !draft);
                }}
              >
                {showCreateCoupon ? "Remove" : "Add New Coupon"}
              </IgButton>
            </span>
          </IgTooltip>
        </Grid>
      </Grid>

      {phase.coupon && !showCreateCoupon ? (
        <Grid item sx={{ minHeight: "50px" }} alignItems={"center"}>
          <CouponSummary index={index} payload={payload} />
        </Grid>
      ) : null}
      {showCreateCoupon ? (
        <Grid item>
          <CreateCoupon
            payload={payload}
            dispatchPayload={dispatchPayload}
            onSuccess={onCouponSuccess}
          />
        </Grid>
      ) : null}
      <Grid item container mt={2} rowSpacing={2}>
        {Object.entries(data).map(([productId, items], index) => (
          <Grid item key={`${productId}-${index}`} xs={12}>
            <PriceAccordion
              productId={productId}
              defaultOpen={!items.every((item) => !!item.quantity)}
              payload={payload}
              dispatchPayload={dispatchPayload}
              items={items}
              allData={allData}
            />
          </Grid>
        ))}
      </Grid>
      <Grid item textAlign={"end"}>
        <IgButton
          igVariant={"stripe"}
          sx={{ width: "87px" }}
          disabled={
            !allData.every((item) => !!item.price?.id && !!item.quantity)
          }
          onClick={() => {
            dispatchPayload({ type: "setTabIndex", value: index + 1 });
          }}
        >
          Continue
        </IgButton>
      </Grid>
    </Grid>
  );
};
